import humanizeDuration from "humanize-duration";
import { uuidv4 } from "@salespark/toolkit";
import { createDpopSigner } from "@salespark/api-client-v2";

/**
 * Sanitizes text to prevent script injection while allowing common HTML formatting
 * @param {string} input - The text to sanitize
 * @returns {string} Sanitized text safe for storage
 */
function preventScriptInjection(input) {
  // Return empty string for null/undefined inputs
  if (input == null) {
    return "";
  }

  // Convert input to string
  input = String(input);

  // List of dangerous patterns to remove
  const dangerousPatterns = [
    // Script tags
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,

    // Dangerous protocols
    /(javascript|data|vbscript):/gi,

    // Event handlers (onclick, onload, etc.)
    /\s+on\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi,

    // Meta characters that could be used to break out of attributes
    /(\%00|\x00)/g, // Null bytes

    // Prevent usage of eval
    /eval\s*\(.*\)/gi,

    // Function constructor
    /Function\s*\(.*\)/gi,

    // setTimeout/setInterval with string argument
    /set(Timeout|Interval)\s*\(\s*["'].*["']\s*\)/gi,

    // Base64 javascript injection
    /data:text\/html.*,/gi,
  ];

  // Apply each dangerous pattern removal
  let sanitized = dangerousPatterns.reduce((text, pattern) => {
    return text.replace(pattern, "");
  }, input);

  // Prevent breaking out of attributes in certain contexts
  sanitized = sanitized.replace(/"/g, "&quot;").replace(/'/g, "&#x27;").replace(/`/g, "&#x60;");

  return sanitized;
}

export const formatTextArea = (text) => {
  try {
    // Change to allow html replacement
    text = preventScriptInjection(text);
    text = text.replace(/(?:\r\n|\r|\n)/g, "<br>");
    return <div dangerouslySetInnerHTML={{ __html: text }} />;

    // Error handling
  } catch (error) {
    return text;
  }
};

export const marketplaceProductSlug = (product_id, product_name) => {
  try {
    if (!product_id || !product_name) return "";
    return `${product_id.toLowerCase()}--${slugify(product_name)}`;

    // Error handling
  } catch (error) {
    console.error("Error generating marketplace product slug", error);
    return product_id;
  }
};

/******************************************************************
 * ##: Marketplace Product URL
 * Build the public SalesPark Marketplace page URL for a product.
 * @param {string} product_id - Product ID
 * @param {string} product_name - Product name (used for the readable slug)
 * @returns {string} - Return the absolute product URL, or an empty string when it cannot be built
 * History:
 * 28-08-2026: Created
 ******************************************************************/
export const marketplaceProductUrl = (product_id, product_name) => {
  try {
    const slug = marketplaceProductSlug(product_id, product_name);
    if (!slug) return "";

    return `${process.env.REACT_APP_MARKETPLACE_URL}/product/${slug}`;

    // Error handling
  } catch (error) {
    console.error("Error generating marketplace product URL", error);
    return "";
  }
};

/**
 * Converts a string into a URL-friendly slug.
 * - Converts to lowercase
 * - Replaces spaces and special characters with hyphens
 * - Removes diacritics (accents)
 * - Removes non-alphanumeric characters
 * - Collapses multiple hyphens into a single hyphen
 * - Trims hyphens from start and end
 *
 * @param {string} str - The string to convert into a slug
 * @param {Object} [options] - Optional configuration
 * @param {boolean} [options.lower=true] - Convert to lowercase
 * @param {string} [options.separator='-'] - Character to use as separator
 * @returns {string} The slugified string
 */
function slugify(str, options = {}) {
  try {
    // Set default options
    const defaults = {
      lower: true,
      separator: "-",
    };

    const opts = { ...defaults, ...options };

    // Convert to string if input is not a string
    str = str.toString();

    // Convert to lowercase if specified
    if (opts.lower) {
      str = str.toLowerCase();
    }

    // Remove accents/diacritics
    str = str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    // Replace spaces and special characters with separator
    str = str
      .replace(/[^a-zA-Z0-9\-\s]+/g, "") // Remove non-alphanumeric chars except hyphens and spaces
      .trim() // Trim whitespace from ends
      .replace(/[\s\-]+/g, opts.separator) // Replace spaces and hyphens with separator
      .replace(new RegExp(`^${opts.separator}+|${opts.separator}+$`, "g"), ""); // Trim separator from ends

    return str;

    // Error handling
  } catch (error) {
    console.error("Error slugifying string", error);
    return str;
  }
}

export const apiTokenScopesOptions = {
  products: { label: "Produtos", active: true },
  pricing: { label: "Preços", active: true },
  orders: { label: "Encomendas", active: true },
  sales: { label: "Vendas", active: true },
  students: { label: "Alunos", active: true },
  buyers: { label: "Compradores", active: false },
  courses: { label: "Cursos", active: false },
  recurrences: { label: "Recorrências", active: false },
  refunds: { label: "Reembolsos", active: false },
  students_auth: { label: "Autenticação Alunos", active: true },
};

export const productFormat = (product_format) => {
  switch (product_format) {
    case "course":
      return "Curso Online";

    case "service":
      return "Serviço";

    case "digital":
      return "Produto Digital";

    case "ebook":
      return "E-Book/Audiobook";

    case "online-event":
      return "Evento Online";

    case "physical-event":
      return "Evento Presencial";

    case "bundle":
      return "Bundle (Pack / Conjunto)";

    case "course-licences":
      return "Curso Online com Licenças";

    case "one-time-service":
      return "Serviço Avulso";

    case "digital-quantity":
      return "Produto Digital Especial";

    default:
      return "Desconhecido";
  }
};

export const hidePartOfEmail = (email) => {
  try {
    if (!email) return "";
    const [username, domain] = email.split("@");
    let sliceSize = username.length > 3 ? 3 : 1;
    const hiddenUsername = username.slice(0, sliceSize) + "*".repeat(username.length - sliceSize);
    // Slice also half of the domain from start of domain
    sliceSize = domain.length > 3 ? Math.ceil(domain.length / 2) : 1;
    const hiddenDomain = "*".repeat(sliceSize) + domain.slice(sliceSize);
    return `${hiddenUsername}@${hiddenDomain}`;
  } catch (e) {
    return email;
  }
};

export const downloadCSV = (csv, name = "export") => {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.setAttribute("href", url);
  a.setAttribute("download", `${name}.csv`);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const downloadXLSX = (xlsx, name = "export") => {
  const blob = new Blob([xlsx], { type: "application/pdf", encoding: "UTF-8" });

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.setAttribute("href", url);
  a.setAttribute("download", `${name}.xlsx`);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const formItemHelp = (text) => {
  return (
    <div className="text-muted">
      <small>
        <em>{text}</em>
      </small>
    </div>
  );
};

export const formatNumber = (value, decimals = 2) => {
  // if (!value || value === undefined) value = 0;
  // value = parseFloat(value);
  // let r = value.toFixed(decimals);

  // var parts = r.toString().split(".");
  // parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  // r = parts.join(".");

  // return r;

  return new Intl.NumberFormat("pt-PT", {
    style: "decimal",
    minimumFractionDigits: decimals,
  }).format(value);
};

export const formatCurrency = (value, currency = null, decimals = 2) => {
  console.log("formatCurrency Deprecated. Use formatCurrencyV2 instead.");
  if (!value || value === undefined) value = 0;
  value = parseFloat(value);
  let r = value.toFixed(decimals);
  //let r = value.toFixed(decimals).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
  var parts = r.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  r = parts.join(".");

  if (!!currency) return `${r}${currency}`;
  return r;
};

/****************************************************
 * ##: Format Currency (Intl NumberFormat)
 * @param {Number} value - Currency value
 * History:
 * 10-06-2024: Created
 * 08-01-2025: Updated (added round and valueToSum)
 ****************************************************/
export const formatCurrencyV2 = (value, withoutCurrencySymbol = false, locales = "pt-PT", round = false, valueToSum = undefined) => {
  try {
    // Check if value is null, undefined or empty
    if (value === undefined || value === null || value === "") value = 0;

    // Check if valueToSum is null, undefined or empty
    if (valueToSum === undefined || valueToSum === null || valueToSum === "") valueToSum = 0;

    // Sum value with valueToSum
    value = value + valueToSum;

    // Round value if needed if requested
    if (round === true) {
      value = (Math.round(value * 100) / 100).toFixed(2);
    }

    const intlOptions = {
      style: withoutCurrencySymbol === true ? "decimal" : "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    };

    return new Intl.NumberFormat(locales, intlOptions).format(value);

    // Error handling
  } catch (error) {
    console.error("Error formatting currency (formatCurrencyV2)", error);
    // If there is an error, fallback to old method
    return formatCurrency(value, "€", 2);
  }
};

export const parseLength = (object, value) => {
  try {
    return object[value].length;
  } catch (error) {
    return 0;
  }
};

export const getFileTypeList = (filetypes_allowed = []) => {
  // setFileContentType(file.type);
  //https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
  //FIXME: JOIN filetypes_allowed
  // Excel files
  let filetypes = [];
  const _excel = [
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel.sheet.macroEnabled.12",
    "application/vnd.ms-excel.sheet.binary.macroEnabled.12",
    "application/vnd.oasis.opendocument.spreadsheet",
  ];

  // Word files
  const _word = [
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/x-abiword",
    "application/vnd.oasis.opendocument.text",
  ];

  // Text files
  const _text = ["text/csv", "text/plain", "text/css", "text/calendar", "application/rtf"];

  // PDF files
  const _pdf = ["application/pdf", "application/epub+zip"];

  // Powerpoint files
  const _powerpoint = [
    "application/vnd.oasis.opendocument.presentation",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ];

  // Audio files
  const _audio = ["audio/aac", "audio/mpeg", "audio/ogg", "audio/opus", "audio/wav", "audio/webm", "audio/m4a", "audio/x-m4a"];

  // Image files
  const _image = [
    "image/jpeg",
    "image/png",
    "image/svg+xml",
    "image/tiff",
    "image/webp",
    "image/bmp",
    "image/avif",
    "image/heif",
    "image/heif-sequence",
    "image/heic",
    "image/heic-sequence",
    "image/avif-sequence",
  ];

  if (filetypes_allowed.includes("excel")) {
    filetypes = filetypes.concat(_excel);
  }
  if (filetypes_allowed.includes("word")) {
    filetypes = filetypes.concat(_word);
  }
  if (filetypes_allowed.includes("text")) {
    filetypes = filetypes.concat(_text);
  }
  if (filetypes_allowed.includes("pdf")) {
    filetypes = filetypes.concat(_pdf);
  }
  if (filetypes_allowed.includes("powerpoint")) {
    filetypes = filetypes.concat(_powerpoint);
  }
  if (filetypes_allowed.includes("audio")) {
    filetypes = filetypes.concat(_audio);
  }
  if (filetypes_allowed.includes("image")) {
    filetypes = filetypes.concat(_image);
  }
  return filetypes;
};

export const convertHexToRGBA = (hexCode, opacity = 1) => {
  let hex = hexCode.replace("#", "");

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  /* Backward compatibility for whole number based opacity values. */
  if (opacity > 1 && opacity <= 100) {
    opacity = opacity / 100;
  }

  return `rgba(${r},${g},${b},${opacity})`;
};

export const setLocalStorage = (section, key, value) => {
  try {
    if (typeof window !== "undefined") {
      localStorage.setItem(`SalesPark-${section}-${key}`, value);
    }
  } catch (error) {
    // do not
  }
};

/**
 * Returns a persistent per-browser client UID (UUID v4), stored in localStorage.
 * Stable across tabs and reloads, so online presence dedups to one point per browser.
 * Falls back to an ephemeral id when localStorage is unavailable (SSR / private mode / blocked).
 * @returns {string} client UID
 */
export const getClientUid = () => {
  const STORAGE_KEY = "SalesPark-client-uid";
  try {
    if (typeof window === "undefined") return uuidv4();
    let uid = localStorage.getItem(STORAGE_KEY);
    if (uid === null || uid === undefined || uid === "") {
      uid = uuidv4();
      localStorage.setItem(STORAGE_KEY, uid);
    }
    return uid;
  } catch (error) {
    return uuidv4();
  }
};

export const getLocalStorage = (section, key, defValue) => {
  try {
    if (typeof window !== "undefined") {
      const r = localStorage.getItem(`SalesPark-${section}-${key}`);
      if (r === null || r === undefined || r === "") {
        return defValue;
      }
      return r;
    }
    return defValue;

    // Error handling
  } catch (error) {
    return defValue;
  }
};

export const isNullOrUndefined = (value) => {
  return value === null || value === undefined;
};

export const isNullUndefinedEmpty = (value) => {
  return value === null || value === undefined || value === "";
};

export const isNullUndefinedEmptyZeroLength = (value) => {
  return value === null || value === undefined || value === "" || value?.length === 0;
};

export const checkObjects = (obj1, obj2) => {
  if (obj1 === obj2) return true; // Direct identity

  // Check types and if both are objects or arrays
  if (typeof obj1 !== "object" || obj1 === null || typeof obj2 !== "object" || obj2 === null) return false;

  // Check if both are arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;
    return obj1.every((el, index) => checkObjects(el, obj2[index]));
  }

  // Check if both are objects
  if (!Array.isArray(obj1) && !Array.isArray(obj2)) {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    return keys1.every((key) => obj2.hasOwnProperty(key) && checkObjects(obj1[key], obj2[key]));
  }

  return false; // Different types (e.g., one is an array and the other is an object)
};

export const checkCapitalization = (text, options = {}) => {
  // Set default options
  const config = {
    threshold: options.threshold || 40,
    minLength: options.minLength || 3,
    excludeWords: options.excludeWords || [],
    ...options,
  };

  // Early returns for invalid input
  if (!text || typeof text !== "string") {
    return {
      status: false,
      data: "Texto inválido",
    };
  }

  // Split text into words and filter out short words and excluded words
  const words = text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length >= config.minLength && !config.excludeWords.includes(word.toLowerCase()));

  if (words.length === 0) {
    return {
      status: true,
      data: "Texto sem palavras para análise",
    };
  }

  // Initialize counters for letter analysis
  let totalLetters = 0;
  let capitalizedLetters = 0;

  // Analyze each word
  words.forEach((word) => {
    // Skip first letter of each word
    const restOfWord = word.slice(1);

    // Count total letters (excluding first character)
    totalLetters += restOfWord.length;

    // Count uppercase letters (excluding first character)
    capitalizedLetters += restOfWord.split("").filter((char) => char.match(/[A-Z]/)).length;
  });

  // Calculate capitalization percentage based on letters
  const capitalizationPercentage = (capitalizedLetters / totalLetters) * 100;

  // Prepare detailed analysis
  const analysis = {
    totalWords: words.length,
    totalLetters: totalLetters,
    capitalizedLetters: capitalizedLetters,
    percentage: parseFloat(capitalizationPercentage.toFixed(2)),
    wordsAnalyzed: words.map((word) => ({
      word,
      caps: word
        .slice(1)
        .split("")
        .filter((char) => char.match(/[A-Z]/)).length,
      total: word.length - 1,
    })),
  };

  // Return result based on threshold
  if (capitalizationPercentage > config.threshold) {
    return {
      status: false,
      data: "Excesso de capitalização",
      analysis,
      details: `${analysis.percentage}% das letras estão em maiúsculas (limite: ${config.threshold}%)`,
    };
  }

  return {
    status: true,
    data: "Capitalização dentro dos limites",
    analysis,
  };
};

export const FormItemHelper = ({ help }) => {
  return (
    <div className="text-muted form-item-helper">
      <small>
        <em>{help}</em>
      </small>
    </div>
  );
};

export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

export const shortPTDuration = humanizeDuration.humanizer({
  language: "shortPT",
  spacer: "",
  conjunction: " ",
  maxDecimalPoints: 0,
  largest: 2,
  languages: {
    shortPT: {
      y: () => "ano",
      mo: () => "mês",
      w: () => "semana",
      d: () => "dia",
      h: () => "h",
      m: () => "m",
      s: () => "s",
      ms: () => "ms",
    },
  },
});

// DPoP (RFC 9449) device-key signer. One instance for the whole app: createDpopSigner holds a single
// non-extractable P-256 key in IndexedDB (shared by every tab), generated lazily on first use. The
// authenticated client passes it as the `dpop` option so every same-origin request carries a proof.
// getToken is read fresh on each proof for the `ath` claim, so it tracks the current session token
// kept in sync by createAPIClientAuth below.
let _dpopToken = null;
const _dpopSigner = createDpopSigner({ getToken: () => _dpopToken });

// Single-session guard. The API answers 401 { status:false, data:{ code:"session_superseded" } } when
// a newer login on another computer/browser took over the one allowed session (DPoP thumbprint changed).
// Surface it ONCE (concurrent in-flight calls all 401 together) as a window event the Auth0 gate
// listens for, so the app shows the "sessão iniciada noutro computador" screen instead of looping
// 401s. Any other 401/403 is left to the calling component, exactly as before.
let _sessionSupersededNotified = false;
const handleApiAuthError = (clientError) => {
  try {
    if (clientError?.response?.status !== 401) return;
    // SalesParkContract body { status, data }: the superseded payload is inside data.
    const payload = clientError?.response?.data?.data;
    if (!payload || payload.code !== "session_superseded") return;
    if (_sessionSupersededNotified) return;
    _sessionSupersededNotified = true;
    window.dispatchEvent(new CustomEvent("sp:session-superseded", { detail: payload.session || null }));
  } catch (error) {
    // Never throw from inside the client's error path.
  }
};

/****************************************************
 * ##: Get DPoP public JWK
 * The four-member P-256 public key to enrol with the API on login (POST /auth). Resolves a
 * { status, data } contract; status false (Safari private mode, no WebCrypto) means skip DPoP.
 * History:
 * 03-10-2026: Created
 ****************************************************/
export const getDpopPublicJwk = () => _dpopSigner.getPublicJwk();

/****************************************************
 * ##: Create API Client Auth
 * @param {Object} authData - Authentication data
 * History:
 * 22-08-2025: Created
 * 25-07-2026: Align return with withAuth ClientOptions ({} on error, string header values)
 * 03-10-2026: Attach DPoP signer and keep its token in sync for the ath claim
 * 25-10-2026: Wire onAuthError so a session_superseded 401 surfaces the "session elsewhere" screen
 ****************************************************/
export const createAPIClientAuth = (authData) => {
  try {
    if (!authData) return {};
    // Keep the DPoP signer token in sync with the session, for the ath claim.
    _dpopToken = authData?.token ?? null;
    return {
      authHeaders: { "x-email": authData?.email, "x-guserid": authData?.guserid, "x-token": authData?.token, "x-token-version": "2" },
      baseURL: process.env.REACT_APP_API_URL,
      dpop: _dpopSigner,
      onAuthError: handleApiAuthError,
    };
  } catch (error) {
    console.error("Error creating API client auth", error);
    return {};
  }
};

/****************************************************
 * ##: Create API Client
 * History:
 * 04-02-2026: Created
 ****************************************************/
export const createAPIClient = () => {
  try {
    return {
      authHeaders: { "x-token-version": 2 },
      baseURL: process.env.REACT_APP_API_URL,
    };
  } catch (error) {
    console.error("Error creating API client", error);
    return null;
  }
};
